import {
  forwardRef,
  useState,
  InputHTMLAttributes,
  useRef,
  useImperativeHandle,
} from 'react';
import { twMerge } from 'src/utils/twMerge';
import { Form } from 'antd';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> & {
  prefix?: React.ReactNode;
};

const baseClasses =
  'group w-full min-h-16 p-4 bg-white rounded-2xl border-2 border-structural-light focus-within:border-primary-darkest focus:border-primary-darkest transition-colors';
const inputBaseClasses =
  'outline-none placeholder:text-structural-light placeholder:font-bold text-base text-structural-darkest font-bold';
const errorClasses = 'border-danger-neutral bg-red-lightest';

const Input = forwardRef<HTMLInputElement, Props>(
  ({ prefix, className, ...props }, ref) => {
    const internalRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => internalRef.current as HTMLInputElement);

    const formItemStatus = Form.Item.useStatus();
    const contextStatus = formItemStatus.status;

    const hasError = contextStatus === 'error';

    if (prefix) {
      return (
        <div
          className={twMerge(
            'relative flex items-center',
            baseClasses,
            hasError && errorClasses,
            className,
          )}
        >
          <span className="mr-1">{prefix}</span>
          <input
            {...props}
            ref={internalRef}
            className={twMerge(inputBaseClasses, 'flex-1 bg-transparent')}
          />
        </div>
      );
    }

    return (
      <input
        {...props}
        ref={internalRef}
        className={twMerge(
          baseClasses,
          inputBaseClasses,
          hasError && errorClasses,
          className,
        )}
      />
    );
  },
);

Input.displayName = 'Input';

type InputPasswordProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> & {
  prefix?: React.ReactNode;
};

export const InputPassword = forwardRef<HTMLInputElement, InputPasswordProps>(
  ({ prefix, className, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const internalRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => internalRef.current as HTMLInputElement);

    const formItemStatus = Form.Item.useStatus();
    const contextStatus = formItemStatus.status;

    const hasError = contextStatus === 'error';

    const togglePassword = () => setShowPassword(!showPassword);

    return (
      <div
        className={twMerge(
          'relative flex items-center gap-2',
          baseClasses,
          hasError && errorClasses,
          className,
        )}
      >
        {prefix && <span className="mr-1">{prefix}</span>}
        <input
          {...props}
          ref={internalRef}
          type={showPassword ? 'text' : 'password'}
          className={twMerge(inputBaseClasses, 'flex-1 bg-transparent')}
        />
        <button
          type="button"
          onClick={togglePassword}
          className="focus:outline-none bg-transparent p-2"
          tabIndex={-1}
        >
          {showPassword ? '🙈' : '👁️'}
        </button>
      </div>
    );
  },
);

InputPassword.displayName = 'InputPassword';

export default Input;
